import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  CircularProgress,
  LinearProgress,
  Divider,
  Alert,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import InfoIcon from '@mui/icons-material/Info';

const PartnerDashboard = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('today');
  const [loading, setLoading] = useState(true);
  const [partnerInfo, setPartnerInfo] = useState(null);
  const [stats, setStats] = useState({});
  const [recentOrders, setRecentOrders] = useState([]);

  const loadPartnerData = useCallback(async () => {
    try {
      const response = await api.get('/partners/dashboard', { params: { period } });
      setPartnerInfo(response.data.partner || null);
      setStats(response.data.stats || {});
      setRecentOrders(response.data.orders || []);
    } catch (err) {
      setPartnerInfo(null);
      setStats({});
      setRecentOrders([]);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadPartnerData();
  }, [loadPartnerData]);

  const getTypeLabel = (type) => {
    const labels = {
      restaurant: 'Restaurant',
      vtc_driver: 'Société VTC',
      store: 'Commerce / Magasin',
      logistics: 'Logistique / Transport',
      car_rental: 'Location de véhicules',
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', pb: 4 }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 4 }}>
        <Container maxWidth="xl">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                Espace Partenaire
              </Typography>
              <Typography variant="h6">
                {partnerInfo.name}
              </Typography>
              <Chip
                label={getTypeLabel(partnerInfo.type)}
                size="small"
                sx={{ bgcolor: 'white', color: 'primary.main', mt: 1 }}
              />
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Taux de commission
              </Typography>
              <Chip
                label={`${partnerInfo.commission_rate}%`}
                color="warning"
                sx={{ fontWeight: 700, fontSize: '1.1rem', px: 2 }}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mt: -4 }}>
        {/* Info commission */}
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
          Usafiri prélève <strong>{partnerInfo.commission_rate}%</strong> de commission sur chaque vente.
          Vous recevez <strong>{100 - partnerInfo.commission_rate}%</strong> du montant total.
        </Alert>

        {/* Filtre période */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Période"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  size="small"
                >
                  <MenuItem value="today">Aujourd'hui</MenuItem>
                  <MenuItem value="week">Cette semaine</MenuItem>
                  <MenuItem value="month">Ce mois</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Statistiques période sélectionnée */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: 'primary.light', width: 56, height: 56 }}>
                    <TrendingUpIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>
                      {stats[`${period}_revenue`].toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Chiffre d'affaires (€)
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: 'warning.light', width: 56, height: 56 }}>
                    <AttachMoneyIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700} color="error.main">
                      -{stats[`${period}_commission`].toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Commission Usafiri (€)
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: 'success.light', width: 56, height: 56 }}>
                    <AccountBalanceWalletIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700} color="success.main">
                      {stats[`${period}_net`].toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Votre part (€)
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: 'info.light', width: 56, height: 56 }}>
                    <ReceiptIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>
                      {stats[`${period}_orders`]}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Commandes
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Détail des revenus */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Répartition des revenus {period === 'today' ? "d'aujourd'hui" : period === 'week' ? 'de la semaine' : 'du mois'}
            </Typography>
            <Box sx={{ mt: 3 }}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Chiffre d'affaires total</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {stats[`${period}_revenue`].toLocaleString()} €
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={100}
                sx={{ height: 10, borderRadius: 1, mb: 2 }}
              />

              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="error.main">
                  Commission Usafiri ({partnerInfo.commission_rate}%)
                </Typography>
                <Typography variant="body2" fontWeight={600} color="error.main">
                  -{stats[`${period}_commission`].toLocaleString()} €
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={partnerInfo.commission_rate}
                color="error"
                sx={{ height: 10, borderRadius: 1, mb: 2 }}
              />

              <Divider sx={{ my: 2 }} />

              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="h6" fontWeight={700}>
                  Votre part nette ({100 - partnerInfo.commission_rate}%)
                </Typography>
                <Typography variant="h6" fontWeight={700} color="success.main">
                  {stats[`${period}_net`].toLocaleString()} €
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={100 - partnerInfo.commission_rate}
                color="success"
                sx={{ height: 10, borderRadius: 1 }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Solde en attente */}
        <Card sx={{ mb: 3, bgcolor: 'warning.lighter' }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Solde en attente de paiement
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sera versé lors du prochain cycle de paiement
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight={700} color="warning.dark">
                {stats.pending_balance.toLocaleString()} €
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Commandes récentes */}
        <Paper>
          <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
            <Typography variant="h6" fontWeight={700}>
              Commandes récentes
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>N° Commande</strong></TableCell>
                  <TableCell><strong>Client</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell align="right"><strong>Montant</strong></TableCell>
                  <TableCell align="right"><strong>Commission</strong></TableCell>
                  <TableCell align="right"><strong>Votre part</strong></TableCell>
                  <TableCell align="center"><strong>Statut</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentOrders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {order.order_number}
                      </Typography>
                    </TableCell>
                    <TableCell>{order.customer}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{order.date}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {order.amount.toLocaleString()} €
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="error.main">
                        -{order.commission.toLocaleString()} €
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={700} color="success.main">
                        {order.net.toLocaleString()} €
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={order.status === 'completed' ? 'Terminé' : 'En cours'}
                        color={order.status === 'completed' ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Actions */}
        <Grid container spacing={2} sx={{ mt: 3 }}>
          <Grid item xs={12} sm={6}>
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={() => navigate('/partner/reports')}
            >
              Télécharger rapport détaillé
            </Button>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={() => navigate('/partner/payouts')}
            >
              Historique des paiements
            </Button>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default PartnerDashboard;
